import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import { deviceService } from '../services/deviceService';

const DeviceEditDialog = ({ 
  open, 
  device,
  onClose, 
  organizationData, 
  currentUser, 
  onSuccess, 
  onError 
}) => {
  const [deviceName, setDeviceName] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [editing, setEditing] = useState(false);

  // New streaming fields
  const [streamUrl, setStreamUrl] = useState('');
  const [streamNetworkId, setStreamNetworkId] = useState('');
  const [streamSectionId, setStreamSectionId] = useState('');

  useEffect(() => {
    if (device) {
      setDeviceName(device.device_name || '');
      setVehicleId(device.vehicle_id || '');
      // Use 'org-wide' as a specific identifier for the organization-wide option
      setSelectedNetwork(device.network_id === null ? 'org-wide' : device.network_id);
      
      // Set streaming fields
      setStreamUrl(device.stream_url || '');
      setStreamNetworkId(device.stream_network_id || '');
      setStreamSectionId(device.stream_section_id || '');
    }
  }, [device]);

  const resetForm = () => {
    setDeviceName('');
    setVehicleId('');
    setSelectedNetwork('');
    // Reset streaming fields
    setStreamUrl('');
    setStreamNetworkId('');
    setStreamSectionId('');
  };

  const handleClose = () => {
    if (!editing) {
      resetForm();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!deviceName.trim() || !vehicleId.trim()) {
      onError('Please fill in all required fields');
      return;
    }

    // Validate streaming fields
    if (streamUrl.trim()) {
      if (!streamNetworkId) {
        onError('Stream network is required when stream URL is provided');
        return;
      }
      // Validate URL format
      if (!streamUrl.match(/^https?:\/\/.+/)) {
        onError('Stream URL must be a valid HTTP or HTTPS URL');
        return;
      }
    }

    setEditing(true);
    try {
      const updateData = {
        device_name: deviceName.trim(),
        vehicle_id: vehicleId.trim(),
        network_id: selectedNetwork === 'org-wide' ? null : selectedNetwork,
        updated_by: currentUser.id
      };

      // Add streaming fields (set to null to remove if empty)
      updateData.stream_url = streamUrl.trim() || null;
      updateData.stream_network_id = streamUrl.trim() ? streamNetworkId : null;
      updateData.stream_section_id = streamUrl.trim() && streamSectionId ? streamSectionId : null;

      await deviceService.updateDevice(device.id, updateData);

      onSuccess(`Device ${deviceName} updated successfully!`);
      resetForm();
      onClose();
    } catch (err) {
      onError(err.message);
    } finally {
      setEditing(false);
    }
  };

  const isFormValid = deviceName.trim() && vehicleId.trim();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <EditIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Edit Device
          </Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Device Name"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="Enter device name"
            fullWidth
            required
            helperText="User-friendly name for the device"
            disabled={editing}
          />
          
          <TextField
            label="Vehicle ID"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            placeholder="Enter vehicle identifier (e.g., license plate)"
            fullWidth
            required
            helperText="Identifier of the vehicle where the device is mounted"
            disabled={editing}
          />
          
          <FormControl fullWidth required disabled={editing}>
            <InputLabel>Network</InputLabel>
            <Select
              value={selectedNetwork}
              onChange={(e) => setSelectedNetwork(e.target.value)}
              label="Network"
              renderValue={(selected) => {
                if (selected === 'org-wide') {
                  return <em>All Networks (Organization-Wide)</em>;
                }
                const network = organizationData?.networks?.find(n => n.id === selected);
                return network ? network.name : '';
              }}
            >
              <MenuItem value="org-wide">
                <em>All Networks (Organization-Wide)</em>
              </MenuItem>
              {organizationData?.networks?.map((network) => (
                <MenuItem key={network.id} value={network.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <RouteIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">{network.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {network.code} • {network.deviceCount} devices
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Live Streaming Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              🔴 Live Streaming
            </Typography>
            
            <Stack spacing={2}>
              <TextField
                label="Stream URL"
                value={streamUrl}
                onChange={(e) => setStreamUrl(e.target.value)}
                placeholder="https://stream-url.example.com/video"
                fullWidth
                helperText="HTTP/HTTPS URL for live video stream from this device (leave empty to remove)"
                disabled={editing}
              />
              
              {streamUrl.trim() && (
                <>
                  <FormControl fullWidth required disabled={editing}>
                    <InputLabel>Stream Network</InputLabel>
                    <Select
                      value={streamNetworkId}
                      onChange={(e) => setStreamNetworkId(e.target.value)}
                      label="Stream Network"
                    >
                      {organizationData?.networks?.map((network) => (
                        <MenuItem key={network.id} value={network.id}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <RouteIcon fontSize="small" />
                            <Box>
                              <Typography variant="body2">{network.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {network.code}
                              </Typography>
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {streamNetworkId && (
                    <FormControl fullWidth disabled={editing}>
                      <InputLabel>Stream Section (Optional)</InputLabel>
                      <Select
                        value={streamSectionId}
                        onChange={(e) => setStreamSectionId(e.target.value)}
                        label="Stream Section (Optional)"
                      >
                        <MenuItem value="">
                          <em>No specific section</em>
                        </MenuItem>
                        {(() => {
                          const selectedNetwork = organizationData?.networks?.find(n => n.id === streamNetworkId);
                          console.log('Selected network for sections:', selectedNetwork);
                          console.log('Available sections:', selectedNetwork?.sections);
                          return selectedNetwork?.sections?.map((section) => (
                            <MenuItem key={section.id} value={section.id}>
                              <Typography variant="body2">{section.name}</Typography>
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </Stack>
          </Box>

          {device && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Device Information
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Device UUID:</Typography>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
                    {device.device_uuid}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    label={device.is_active ? 'Active' : 'Registered'} 
                    size="small"
                    color={device.is_active ? 'success' : 'info'}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Stack>
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={editing}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={editing || !isFormValid}
          startIcon={editing ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {editing ? 'Updating...' : 'Update Device'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceEditDialog; 